import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { GlobalEvent } from '@/mocks/calendarEntries';

interface AwarenessEventsMasterProps {
  events: GlobalEvent[];
  onAdd: (event: Omit<GlobalEvent, 'id' | 'color' | 'type'>) => void;
  onEdit: (id: string, event: Omit<GlobalEvent, 'id' | 'color' | 'type'>) => void;
  onDelete: (id: string) => void;
}

export default function AwarenessEventsMaster({ events, onAdd, onEdit, onDelete }: AwarenessEventsMasterProps) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    eventName: '',
    date: '',
    isRecurringAnnual: true,
    targetAudience: 'All' as 'All' | 'Teachers' | 'Parents',
    description: '',
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setFormData({ eventName: '', date: '', isRecurringAnnual: true, targetAudience: 'All', description: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = () => {
    if (!formData.eventName.trim() || !formData.date.trim()) return;
    const payload = { ...formData, eventName: formData.eventName.trim(), date: formData.date.trim(), description: formData.description.trim() };
    if (editingId) {
      onEdit(editingId, payload);
    } else {
      onAdd(payload);
    }
    resetForm();
  };

  const startEdit = (event: GlobalEvent) => {
    setFormData({
      eventName: event.eventName,
      date: event.date,
      isRecurringAnnual: event.isRecurringAnnual,
      targetAudience: event.targetAudience,
      description: event.description || '',
    });
    setEditingId(event.id);
    setShowForm(true);
  };

  const handleCsvImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const lines = text.split('\n').filter((l) => l.trim());
      lines.slice(1).forEach((line) => {
        const cols = line.split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
        if (cols.length >= 5) {
          onAdd({
            eventName: cols[0],
            date: cols[1],
            isRecurringAnnual: cols[2].toLowerCase() === 'true',
            targetAudience: (['All', 'Teachers', 'Parents'].includes(cols[3]) ? cols[3] : 'All') as 'All' | 'Teachers' | 'Parents',
            description: cols.slice(4).join(', '),
          });
        }
      });
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div>
      {/* Header actions */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <p className="text-sm text-foreground-500 font-label">{t('dashboard.awarenessEventsDescription')}</p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-background-100 rounded-lg text-sm font-label font-medium text-foreground-600 hover:text-foreground-800 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-upload-2-line text-base" />
            {t('dashboard.importCsv')}
          </button>
          <input ref={fileInputRef} type="file" accept=".csv" onChange={handleCsvImport} className="hidden" />
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line text-base" />
            {t('dashboard.addEvent')}
          </button>
        </div>
      </div>

      {/* CSV Hint */}
      <div className="mb-4 p-3 bg-background-100 rounded-lg text-xs text-foreground-500 font-label">
        <i className="ri-information-line mr-1" />
        {t('dashboard.csvImportHint')}
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="mb-4 p-4 bg-background-100 rounded-xl border border-background-200/70">
          <h4 className="font-label font-semibold text-foreground-800 text-sm mb-3">
            {editingId ? t('dashboard.editEvent') : t('dashboard.addEvent')}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-xs font-label font-medium text-foreground-600 mb-1">{t('dashboard.eventName')} *</label>
              <input
                type="text"
                value={formData.eventName}
                onChange={(e) => setFormData((p) => ({ ...p, eventName: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg outline-none focus:border-primary-400 text-foreground-900"
                placeholder="e.g. World Autism Day"
              />
            </div>
            <div>
              <label className="block text-xs font-label font-medium text-foreground-600 mb-1">{t('dashboard.eventDate')} *</label>
              <input
                type="text"
                value={formData.date}
                onChange={(e) => setFormData((p) => ({ ...p, date: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg outline-none focus:border-primary-400 text-foreground-900"
                placeholder="DD/MM"
              />
            </div>
            <div>
              <label className="block text-xs font-label font-medium text-foreground-600 mb-1">{t('dashboard.isRecurring')}</label>
              <select
                value={formData.isRecurringAnnual ? 'true' : 'false'}
                onChange={(e) => setFormData((p) => ({ ...p, isRecurringAnnual: e.target.value === 'true' }))}
                className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg outline-none focus:border-primary-400 text-foreground-900 cursor-pointer"
              >
                <option value="true">{t('common.yes')}</option>
                <option value="false">{t('common.no')}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-label font-medium text-foreground-600 mb-1">{t('dashboard.targetAudience')}</label>
              <select
                value={formData.targetAudience}
                onChange={(e) => setFormData((p) => ({ ...p, targetAudience: e.target.value as 'All' | 'Teachers' | 'Parents' }))}
                className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg outline-none focus:border-primary-400 text-foreground-900 cursor-pointer"
              >
                <option value="All">{t('dashboard.targetAll')}</option>
                <option value="Teachers">{t('dashboard.targetTeachers')}</option>
                <option value="Parents">{t('dashboard.targetParents')}</option>
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="block text-xs font-label font-medium text-foreground-600 mb-1">{t('dashboard.eventDescription')}</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg outline-none focus:border-primary-400 text-foreground-900"
                placeholder="Brief description..."
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSubmit}
              disabled={!formData.eventName.trim() || !formData.date.trim()}
              className="px-4 py-2 bg-primary-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
            >
              {editingId ? t('common.save') : t('common.add')}
            </button>
            <button onClick={resetForm} className="px-4 py-2 text-sm font-label font-medium text-foreground-500 hover:text-foreground-700 rounded-lg cursor-pointer whitespace-nowrap">
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {events.length === 0 ? (
        <div className="text-center py-10 text-foreground-400 text-sm font-label">{t('dashboard.noEventsDefined')}</div>
      ) : (
        <div className="overflow-x-auto border border-background-200/70 rounded-xl">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-background-100 text-left">
                <th className="px-4 py-3 font-label font-semibold text-foreground-600 text-xs uppercase tracking-wide">#ID</th>
                <th className="px-4 py-3 font-label font-semibold text-foreground-600 text-xs uppercase tracking-wide">{t('dashboard.eventName')}</th>
                <th className="px-4 py-3 font-label font-semibold text-foreground-600 text-xs uppercase tracking-wide">{t('dashboard.eventDate')}</th>
                <th className="px-4 py-3 font-label font-semibold text-foreground-600 text-xs uppercase tracking-wide">{t('dashboard.isRecurring')}</th>
                <th className="px-4 py-3 font-label font-semibold text-foreground-600 text-xs uppercase tracking-wide">{t('dashboard.targetAudience')}</th>
                <th className="px-4 py-3 font-label font-semibold text-foreground-600 text-xs uppercase tracking-wide">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <tr key={ev.id} className="border-t border-background-200/70 hover:bg-background-50/50 transition-colors">
                  <td className="px-4 py-3 text-foreground-500 font-mono text-xs">{ev.id}</td>
                  <td className="px-4 py-3 text-foreground-800 font-label font-medium">{ev.eventName}</td>
                  <td className="px-4 py-3 text-foreground-600 font-label">{ev.date}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-label font-medium ${ev.isRecurringAnnual ? 'bg-green-100 text-green-700' : 'bg-background-200 text-foreground-500'}`}>
                      {ev.isRecurringAnnual ? t('common.yes') : t('common.no')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-foreground-600 font-label text-xs">{t(`dashboard.target${ev.targetAudience}`)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => startEdit(ev)} className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer" title={t('common.edit')}>
                        <i className="ri-pencil-line text-sm" />
                      </button>
                      <button onClick={() => onDelete(ev.id)} className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer" title={t('common.delete')}>
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}